import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";

const ArrowIcon = ({ className = "" }) => (
  <svg
    className={`w-6 h-6 transition-transform duration-300 group-hover:translate-x-2 ${className}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2.5}
      d="M17 8l4 4m0 0l-4 4m4-4H3"
    />
  </svg>
);

const SoccerBallIcon = ({ className = "", style = {} }) => (
  <svg
    className={`w-8 h-8 ${className}`}
    style={style}
    fill="currentColor"
    viewBox="0 0 24 24"
  >
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
  </svg>
);

const BasketballIcon = ({ className = "", style = {} }) => (
  <svg
    className={`w-6 h-6 ${className}`}
    style={style}
    fill="currentColor"
    viewBox="0 0 24 24"
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
    />
    <path
      d="M12 2v20M2 12h20M6.5 6.5c3 3 8 3 11 0M6.5 17.5c3-3 8-3 11 0"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
  </svg>
);

const TrophyIcon = ({ className = "", style = {} }) => (
  <svg
    className={`w-6 h-6 ${className}`}
    style={style}
    fill="currentColor"
    viewBox="0 0 24 24"
  >
    <path d="M7 4V2C7 1.45 7.45 1 8 1H16C16.55 1 17 1.45 17 2V4H20.38C20.77 4 21.08 4.35 21.03 4.74L20.56 9.26C20.25 11.69 18.2 13.5 15.74 13.5H14.5V17H17C17.55 17 18 17.45 18 18S17.55 19 17 19H7C6.45 19 6 18.55 6 18S6.45 17 7 17H9.5V13.5H8.26C5.8 13.5 3.75 11.69 3.44 9.26L2.97 4.74C2.92 4.35 3.23 4 3.62 4H7Z" />
  </svg>
);

const InlineButton = ({
  dark = false,
  style = {},
  disabled = false,
  onClick,
  children,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      style={!disabled ? style : {}}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`group relative py-5 px-8 capitalize w-full flex items-center gap-4 justify-center rounded-2xl 
        font-bold text-xl tracking-wide overflow-hidden
        transition-all duration-500 ease-out transform
        hover:scale-105 hover:shadow-[0_25px_50px_rgba(34,197,94,0.4)] active:scale-95
        disabled:bg-gray-200 disabled:text-gray-400 disabled:opacity-50 disabled:hover:scale-100
        border-2 border-green-400/30 backdrop-blur-lg
        bg-gradient-to-r from-green-600 via-emerald-500 to-green-500
        hover:from-green-500 hover:via-emerald-400 hover:to-green-400
        shadow-[0_15px_35px_rgba(34,197,94,0.3)]
        ${isHovered ? "shadow-[0_30px_60px_rgba(34,197,94,0.5)]" : ""}`}
    >
      <span className="relative z-10 flex items-center gap-4 text-white drop-shadow-lg">
        {children}
        <ArrowIcon />
      </span>

      {/* Animated shine effect */}
      <div
        className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/20 to-transparent 
                      transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"
      />

      {/* Glow effect */}
      <div
        className="absolute inset-0 rounded-2xl bg-gradient-to-r from-green-400/20 to-emerald-400/20 
                      opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      />
    </button>
  );
};

const WelcomeLayout = () => {
  const [animation, setAnimation] = useState(false);
  const [textAnimation, setTextAnimation] = useState(false);
  const [ballAnimation, setBallAnimation] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Configuración para la app deportiva
  const welcomeConfig = {
    title: "Bienvenido a DP GT",
    subtitle:
      "La red social deportiva definitiva. Conecta, comparte y disfruta del deporte como nunca antes",
    button: "Comenzar Ahora",
    background:
      "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimation(true);
      setTimeout(() => setTextAnimation(true), 300);
      setTimeout(() => setBallAnimation(true), 600);
    }, 10);
    return () => clearTimeout(timer);
  }, []);

  const handleCtaClick = () => {
    navigate("/gta-app/login");
  };

  const isLogin = location.pathname === "/login";

  return (
    <div className="relative overflow-hidden min-h-screen">
      {animation && (
        <section
          style={{ backgroundImage: `url(${welcomeConfig.background})` }}
          className={`fixed inset-0 bg-cover bg-center transition-all duration-700 ease-out transform
            ${
              isLogin
                ? "scale-95 opacity-30 blur-sm"
                : "scale-100 opacity-100 blur-0"
            }`}
        >
          {/* Overlays deportivos */}
          <div className="absolute inset-0 bg-gradient-to-br from-green-900/70 via-blue-800/60 to-green-800/80" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-green-900/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/30 via-transparent to-green-900/40" />

          {/* Animated sports particles */}
          <div className="absolute inset-0 opacity-70">
            {[...Array(30)].map((_, i) => (
              <div
                key={i}
                className={`absolute w-3 h-3 rounded-full animate-bounce
                  ${
                    i % 4 === 0
                      ? "bg-green-400"
                      : i % 4 === 1
                      ? "bg-blue-400"
                      : i % 4 === 2
                      ? "bg-white"
                      : "bg-yellow-400"
                  }`}
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  animationDuration: `${2 + Math.random() * 3}s`,
                }}
              />
            ))}
          </div>

          {/* Floating sports icons */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-30">
            <SoccerBallIcon
              className="absolute top-1/6 left-1/5 text-white animate-spin"
              style={{ animationDuration: "8s" }}
            />
            <BasketballIcon
              className="absolute top-1/4 right-1/4 text-orange-300 animate-bounce"
              style={{ animationDuration: "3s" }}
            />
            <TrophyIcon
              className="absolute bottom-1/3 left-1/4 text-yellow-300 animate-pulse"
              style={{ animationDuration: "2s" }}
            />
            <SoccerBallIcon
              className="absolute top-1/2 right-1/6 text-green-300 animate-spin"
              style={{ animationDuration: "6s", animationDirection: "reverse" }}
            />
            <BasketballIcon
              className="absolute bottom-1/4 right-1/3 text-blue-300 animate-bounce"
              style={{ animationDuration: "4s" }}
            />

            {/* Stadium lights effect */}
            <div
              className="absolute top-1/8 left-1/3 w-40 h-40 border-2 border-green-400/20 rounded-full 
                           animate-pulse bg-gradient-to-r from-green-400/10 to-blue-400/10"
              style={{ animationDuration: "3s" }}
            />
            <div
              className="absolute bottom-1/6 right-1/4 w-32 h-32 border border-blue-400/20 rounded-full 
                           animate-spin bg-gradient-to-r from-blue-400/10 to-green-400/10"
              style={{ animationDuration: "10s" }}
            />
          </div>

          {/* Stadium floodlight rays */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className={`absolute top-0 w-2 h-full bg-gradient-to-b from-white/20 to-transparent animate-pulse`}
                style={{
                  left: `${10 + i * 10}%`,
                  transform: `rotate(${-15 + i * 4}deg)`,
                  animationDelay: `${i * 0.3}s`,
                  animationDuration: `${3 + Math.random() * 2}s`,
                }}
              />
            ))}
          </div>

          {/* Moving gradient waves */}
          <div className="absolute inset-0 opacity-20">
            <div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-green-500/30 to-transparent animate-pulse transform translate-x-full"
              style={{ animation: "slideWave 8s infinite linear" }}
            />
            <div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/30 to-transparent animate-pulse transform -translate-x-full"
              style={{ animation: "slideWave 10s infinite linear reverse" }}
            />
          </div>

          <article
            className={`bottom-0 fixed w-full text-white flex flex-col p-6 py-12 z-20
                              transform transition-all duration-1000 ease-out
                              ${
                                textAnimation
                                  ? "translate-y-0 opacity-100"
                                  : "translate-y-12 opacity-0"
                              }`}
          >
            <div className="flex items-center justify-center mb-6">
              {ballAnimation && (
                <SoccerBallIcon
                  className="text-green-400 w-16 h-16 animate-spin mr-4"
                  style={{ animationDuration: "3s" }}
                />
              )}
              <h1
                className={`font-bold text-5xl md:text-7xl leading-tight
                             bg-gradient-to-r from-green-300 via-emerald-200 to-blue-300 bg-clip-text text-transparent
                             drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]
                             transform transition-all duration-1200 delay-200 ease-out
                             ${
                               textAnimation
                                 ? "translate-y-0 opacity-100"
                                 : "translate-y-8 opacity-0"
                             }`}
              >
                DP GT
              </h1>
              {ballAnimation && (
                <BasketballIcon
                  className="text-orange-400 w-12 h-12 animate-bounce ml-4"
                  style={{ animationDuration: "2s" }}
                />
              )}
            </div>

            <h2
              className={`font-serif font-bold text-2xl md:text-4xl leading-tight mb-3 text-center
                           bg-gradient-to-r from-white via-green-200 to-blue-200 bg-clip-text text-transparent
                           drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]
                           transform transition-all duration-1200 delay-400 ease-out
                           ${
                             textAnimation
                               ? "translate-y-0 opacity-100"
                               : "translate-y-8 opacity-0"
                           }`}
            >
              {welcomeConfig.title}
            </h2>

            <p
              className={`text-lg md:text-xl mt-4 mb-10 leading-relaxed font-medium text-center
                          bg-gradient-to-r from-gray-200 via-green-200 to-blue-200 bg-clip-text text-transparent
                          drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]
                          transform transition-all duration-1200 delay-600 ease-out
                          ${
                            textAnimation
                              ? "translate-y-0 opacity-100"
                              : "translate-y-8 opacity-0"
                          }`}
            >
              {welcomeConfig.subtitle}
            </p>

            <div
              className={`transform transition-all duration-1200 delay-800 ease-out
                            ${
                              textAnimation
                                ? "translate-y-0 opacity-100"
                                : "translate-y-8 opacity-0"
                            }`}
            >
              <InlineButton
                onClick={handleCtaClick}
                style={{
                  background:
                    "linear-gradient(135deg, #22c55e 0%, #10b981 50%, #059669 100%)",
                  color: "#ffffff",
                }}
              >
                <TrophyIcon className="w-6 h-6" />
                {welcomeConfig.button}
              </InlineButton>
            </div>
          </article>

          {/* Animated field lines */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-pulse" />
          <div
            className="absolute bottom-4 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-400/50 to-transparent animate-pulse"
            style={{ animationDelay: "1s" }}
          />
        </section>
      )}

      <div
        className={`transition-all duration-700 ease-out ${
          isLogin ? "relative z-30" : ""
        }`}
      >
        <Outlet />
      </div>

      <style jsx>{`
        @keyframes slideWave {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </div>
  );
};

export default WelcomeLayout;
